import {
  deleteMutation,
  getAllMutations,
  putMutation,
} from "@/lib/offline/offlineDb";
import type {
  CreateIssuePayload,
  OfflineMutation,
  TaskNotePayload,
  TaskPhotoPayload,
  ToggleTaskPayload,
} from "@/lib/offline/offlineTypes";

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function enqueueMutation(
  mutation: Omit<OfflineMutation, "id" | "createdAt"> & { id?: string; createdAt?: number },
): Promise<OfflineMutation> {
  const full: OfflineMutation = {
    id: mutation.id ?? newId(),
    createdAt: mutation.createdAt ?? Date.now(),
    type: mutation.type,
    payload: mutation.payload,
    photoBlobId: mutation.photoBlobId,
  };

  if (full.type === "toggle_task") {
    const payload = full.payload as ToggleTaskPayload;
    const existing = await getAllMutations();
    const previous = existing.find(
      (item) =>
        item.type === "toggle_task" &&
        (item.payload as ToggleTaskPayload).originTaskId === payload.originTaskId &&
        (item.payload as ToggleTaskPayload).stepId === payload.stepId,
    );
    if (previous) {
      await deleteMutation(previous.id);
    }
  }

  if (full.type === "task_note") {
    const payload = full.payload as TaskNotePayload;
    const existing = await getAllMutations();
    const previous = existing.find(
      (item) =>
        item.type === "task_note" &&
        (item.payload as TaskNotePayload).originTaskId === payload.originTaskId &&
        (item.payload as TaskNotePayload).stepId === payload.stepId,
    );
    if (previous) {
      await deleteMutation(previous.id);
    }
  }

  await putMutation(full);
  return full;
}

export async function enqueueToggleTask(payload: ToggleTaskPayload): Promise<OfflineMutation> {
  return enqueueMutation({ type: "toggle_task", payload });
}

export async function enqueueTaskPhoto(
  payload: TaskPhotoPayload,
  photoBlobId: string,
): Promise<OfflineMutation> {
  return enqueueMutation({ type: "task_photo", payload, photoBlobId });
}

export async function enqueueTaskNote(payload: TaskNotePayload): Promise<OfflineMutation> {
  return enqueueMutation({ type: "task_note", payload });
}

export async function enqueueIssue(
  payload: CreateIssuePayload,
  photoBlobId?: string,
): Promise<OfflineMutation> {
  return enqueueMutation({ type: "create_issue", payload, photoBlobId });
}

export { getAllMutations, deleteMutation };
